import { useCallback, useEffect, useRef, useState } from 'react';
import { isImageAttachment } from '../common/chatUploads';
import { putWithProgress } from '../common/r2Put';

/**
 * Subida genérica a R2 con URL prefirmada, progreso y cancelación.
 * El adjunto queda "staged" hasta que el consumidor lo envía o lo descarta.
 *
 * @param {object} config
 * @param {(file: File) => { valid: boolean, mimeType?: string, message?: string }} config.validateFile
 * @param {(file: File, mimeType: string) => Promise<object>} config.requestPresign
 */
export const useR2Upload = ({ validateFile, requestPresign }) => {
    const [attachment, setAttachment] = useState(null);
    const xhrRef = useRef(null);
    const previewUrlRef = useRef(null);

    const releasePreview = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
    }, []);

    const clearAttachment = useCallback(() => {
        xhrRef.current?.abort();
        xhrRef.current = null;
        releasePreview();
        setAttachment(null);
    }, [releasePreview]);

    useEffect(() => clearAttachment, [clearAttachment]);

    const startUpload = useCallback(async (file) => {
        if (!file) return { ok: false };

        const validation = validateFile(file);
        if (!validation.valid) return { ok: false, message: validation.message };

        const { mimeType } = validation;
        xhrRef.current?.abort();
        releasePreview();

        const previewUrl = isImageAttachment(mimeType) ? URL.createObjectURL(file) : null;
        previewUrlRef.current = previewUrl;

        setAttachment({
            fileName: file.name,
            mimeType,
            size: file.size,
            previewUrl,
            status: 'uploading',
            progress: 0,
            payload: null,
        });

        try {
            const presign = await requestPresign(file, mimeType);

            if (!presign?.enabled || !presign?.uploadUrl) {
                throw new Error(presign?.message || 'La subida de archivos no está disponible.');
            }

            await putWithProgress({
                uploadUrl: presign.uploadUrl,
                file,
                mimeType,
                onProgress: (progress) => {
                    setAttachment((prev) => (prev ? { ...prev, progress } : prev));
                },
                onXhr: (xhr) => { xhrRef.current = xhr; },
            });

            xhrRef.current = null;
            setAttachment((prev) => (prev ? {
                ...prev,
                status: 'ready',
                progress: 100,
                payload: {
                    fileName: file.name,
                    mimeType,
                    size: file.size,
                    provider: presign.provider || 'r2',
                    storageKey: presign.storageKey,
                    url: presign.publicUrl,
                },
            } : prev));

            return { ok: true };
        } catch (error) {
            xhrRef.current = null;

            if (error?.aborted) return { ok: false };

            releasePreview();
            setAttachment(null);

            return {
                ok: false,
                message: error?.response?.data?.message || error?.message || 'No se pudo adjuntar el archivo.',
            };
        }
    }, [validateFile, requestPresign, releasePreview]);

    return {
        attachment,
        isUploading: attachment?.status === 'uploading',
        startUpload,
        clearAttachment,
    };
};
